import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Check, ArrowRight, Zap, Globe, ShieldCheck, BarChart3, Palette, Package, CreditCard, ChevronDown, ChevronUp, ChevronLeft, ChevronRight as ChevronRightIcon, Star, X, Wand2, BookOpen, Users, Mail, MousePointerClick, Layers, Bot, Clock } from 'lucide-react';
import { useState, useEffect, useRef, useCallback } from 'react';

import MarketingNav from '../components/MarketingNav';

const API = process.env.REACT_APP_BACKEND_URL;

// LP Analytics tracker — preserved from original
function useLPTracker() {
  const sessionId = useRef(sessionStorage.getItem('lp_sid') || (() => { const id = Math.random().toString(36).slice(2, 12); sessionStorage.setItem('lp_sid', id); return id; })());
  const startTime = useRef(Date.now());
  const sectionTimers = useRef({});
  const getDevice = () => /Mobi|Android/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop';
  const getOS = () => { const ua = navigator.userAgent; if (/iPhone|iPad|iPod/.test(ua)) return 'iOS'; if (/Android/.test(ua)) return 'Android'; if (/Mac/.test(ua)) return 'macOS'; if (/Win/.test(ua)) return 'Windows'; return 'Other'; };
  const track = useCallback((type, extra = {}) => {
    fetch(`${API}/api/lp/track`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type, session_id: sessionId.current, device: getDevice(), os: getOS(), ...extra }) }).catch(() => {});
  }, []);
  useEffect(() => { track('pageview'); const handleUnload = () => { navigator.sendBeacon(`${API}/api/lp/track`, JSON.stringify({ type: 'session_end', session_id: sessionId.current, device: getDevice(), os: getOS(), duration: (Date.now() - startTime.current) / 1000 })); }; window.addEventListener('beforeunload', handleUnload); return () => window.removeEventListener('beforeunload', handleUnload); }, [track]);
  useEffect(() => { const sections = document.querySelectorAll('[data-lp-section]'); const observer = new IntersectionObserver((entries) => { entries.forEach(entry => { const name = entry.target.dataset.lpSection; if (entry.isIntersecting) { sectionTimers.current[name] = Date.now(); } else if (sectionTimers.current[name]) { const dur = (Date.now() - sectionTimers.current[name]) / 1000; if (dur > 0.5) track('section_view', { section: name, duration: dur }); delete sectionTimers.current[name]; } }); }, { threshold: 0.3 }); sections.forEach(s => observer.observe(s)); return () => observer.disconnect(); }, [track]);
  const trackClick = useCallback((button) => track('click', { button }), [track]);
  return { trackClick };
}

// Scroll animation hook
function useScrollAnimate() {
  const ref = useRef(null);
  useEffect(() => { const el = ref.current; if (!el) return; const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.style.opacity = '1'; el.style.transform = 'translateY(0)'; } }, { threshold: 0.1 }); obs.observe(el); return () => obs.disconnect(); }, []);
  return ref;
}
function Reveal({ children, className = '', delay = 0 }) {
  const ref = useScrollAnimate();
  return <div ref={ref} className={className} style={{ opacity: 0, transform: 'translateY(32px)', transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s` }}>{children}</div>;
}

const FEATURES_GRID = [
  { icon: CreditCard, title: 'Multi-Gateway Checkout', desc: 'Billplz, ToyyibPay, SenangPay, Stripe, Midtrans, Xendit. Accept payments globally.' },
  { icon: BarChart3, title: 'Sales Analytics', desc: 'Revenue, visitors, conversion rates. The numbers you need to grow.' },
  { icon: MousePointerClick, title: 'Checkout Forms', desc: 'Embed checkout on any website. 3 lines of code. Sell from anywhere.' },
  { icon: Layers, title: 'Course & Bundle Support', desc: 'Sell courses, bundles, and subscriptions. All product types in one place.' },
  { icon: Package, title: 'Instant Delivery', desc: 'Auto-generate secure download links with expiry. Zero manual work.' },
  { icon: ShieldCheck, title: 'Customer Management', desc: 'Track orders, manage access, reissue downloads. Full control.' },
];

const PRICING = [
  { name: 'Pro', price: '69', period: '/month', desc: 'Everything unlimited. No limits.',
    features: ['Unlimited products', 'Unlimited landing pages', 'Unlimited checkout forms', 'Sales analytics & reports', 'Instant file delivery', 'Multi-gateway checkout', 'Customer management'],
    notIncluded: ['AI Builder & Studio'],
    cta: 'Start 7-Day Free Trial', popular: false, ctaAction: 'register' },
  { name: 'Premium', price: '99', period: '/month', desc: 'Unlimited + full AI access',
    features: ['Everything in Pro', 'AI Landing Page Builder', 'AI Digital Studio', 'AI Growth Consultant', 'AI Copywriting', 'Priority support'],
    notIncluded: [],
    cta: 'Start Premium', popular: true, ctaAction: 'register' },
  { name: 'Custom', price: '999', period: '', desc: 'Everything done for you',
    features: ['Everything in Premium', 'Custom page design', 'Product setup & migration', 'Payment gateway setup', 'Dedicated account manager', 'Priority onboarding'],
    notIncluded: [],
    cta: 'Contact Us', popular: false, ctaAction: 'contact' },
];

const TESTIMONIALS = [
  { name: 'Sarah A.', role: 'Content Creator', text: 'I set up my store in 8 minutes. Literally. My first sale came the same day.', avatar: 'S' },
  { name: 'Amin R.', role: 'Course Creator', text: 'Stopped paying for 4 different tools. Funnely does everything I need in one place.', avatar: 'A' },
  { name: 'Lina M.', role: 'Designer', text: 'The checkout form embed is a game changer. I put it on my portfolio and sales doubled.', avatar: 'L' },
  { name: 'Hafiz K.', role: 'Photographer', text: 'FPX support out of the box. My Malaysian customers love it.', avatar: 'H' },
  { name: 'Nurul I.', role: 'Ebook Author', text: 'I was selling on WhatsApp before. Now my store runs on autopilot.', avatar: 'N' },
  { name: 'Danial Z.', role: 'Video Editor', text: 'The AI page builder created a better landing page than I could in hours.', avatar: 'D' },
];

const FAQS = [
  { q: 'How fast can I start selling?', a: 'Under 10 minutes. Create a product, build a page, share the link. Done.' },
  { q: 'Do I need technical skills?', a: 'None. If you can post on social media, you can use Funnely.' },
  { q: 'What payment methods are supported?', a: 'FPX, credit/debit cards, e-wallets via Billplz, SenangPay, ToyyibPay, Stripe, Midtrans, and Xendit.' },
  { q: 'Is there a transaction fee?', a: 'Funnely charges 0% platform fee. You only pay your payment gateway fees.' },
  { q: 'Can I cancel anytime?', a: 'Yes. No lock-in. Cancel with one click.' },
  { q: 'What can I sell?', a: 'Ebooks, courses, templates, presets, guides, audio, video — any digital file.' },
];

export default function MarketingPage() {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const [slidesPerView, setSlidesPerView] = useState(3);
  const { trackClick } = useLPTracker();

  // Hidden entries — preserved
  const logoClicks = useRef(0); const logoTimer = useRef(null);
  const handleLogoClick = () => { logoClicks.current++; clearTimeout(logoTimer.current); logoTimer.current = setTimeout(() => { logoClicks.current = 0; }, 3000); if (logoClicks.current >= 5) { logoClicks.current = 0; navigate('/lpdashboard'); } };
  const heroClicks = useRef(0); const heroTimer = useRef(null);
  const handleHeroClick = () => { heroClicks.current++; clearTimeout(heroTimer.current); heroTimer.current = setTimeout(() => { heroClicks.current = 0; }, 3000); if (heroClicks.current >= 5) { heroClicks.current = 0; navigate('/admin'); } };

  useEffect(() => { const u = () => { if (window.innerWidth < 640) setSlidesPerView(1); else if (window.innerWidth < 1024) setSlidesPerView(2); else setSlidesPerView(3); }; u(); window.addEventListener('resize', u); return () => window.removeEventListener('resize', u); }, []);
  const maxIdx = Math.max(0, TESTIMONIALS.length - slidesPerView);
  useEffect(() => { const i = setInterval(() => setTestimonialIdx(p => p >= maxIdx ? 0 : p + 1), 5000); return () => clearInterval(i); }, [maxIdx]);

  const goReg = () => navigate('/register');

  return (
    <div className="min-h-screen" style={{ fontFamily: 'Lexend, sans-serif' }}>
      <style>{`
        @keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
      `}</style>

      {/* ══════════ NAV ══════════ */}
      <MarketingNav activePage="/" />

      {/* ══════════ HERO ══════════ */}
      <section data-lp-section="hero" className="relative pt-28 pb-20 sm:pt-36 sm:pb-28 px-4 overflow-hidden" style={{ backgroundColor: '#195259' }}>
        <img src="/images/marketing/element-teal-right.png" alt="" className="absolute top-0 right-0 w-[140px] sm:w-[200px] lg:w-[320px] xl:w-[400px] h-auto pointer-events-none select-none" style={{ zIndex: 1 }} />
        <img src="/images/marketing/element-teal-left.png" alt="" className="absolute bottom-0 left-0 w-[120px] sm:w-[180px] lg:w-[280px] xl:w-[340px] h-auto pointer-events-none select-none" style={{ zIndex: 1 }} />

        <div className="relative max-w-7xl mx-auto" style={{ zIndex: 2 }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-6 items-center">
            {/* Left: Mascot */}
            <div className="text-center lg:text-left">
              <img src="/images/marketing/hero-mascot.png" alt="" loading="eager" decoding="async" fetchpriority="high" className="w-[400px] sm:w-[520px] lg:w-full lg:max-w-[640px] h-auto mx-auto lg:mx-0 object-contain cursor-default" style={{ animation: 'float 4s ease-in-out infinite' }} onClick={handleHeroClick} />
            </div>

            {/* Right: Headline + CTAs */}
            <div className="text-center lg:text-left">
              <Reveal>
                <p className="text-sm font-semibold uppercase tracking-[3px] mb-6" style={{ color: '#FF9A00' }}>Built for digital creators</p>
              </Reveal>
              <Reveal delay={0.1}>
                <h1 className="font-extrabold tracking-tight leading-[1.05] mb-6 text-white text-5xl sm:text-6xl lg:text-[5.5rem] xl:text-[7rem]">
                  Scale Without Limits.
                </h1>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="text-lg sm:text-xl max-w-2xl mx-auto lg:mx-0 mb-10 leading-relaxed" style={{ color: '#F5E9CF', opacity: 0.85 }}>
                  Build AI-powered sales pages, create digital products, and start selling in minutes.
                </p>
              </Reveal>
              <Reveal delay={0.3}>
                <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4">
                  <Button size="lg" className="text-base px-10 h-14 font-semibold text-[#195259] rounded-full shadow-lg hover:shadow-xl transition-shadow" style={{ backgroundColor: '#FF9A00' }} onClick={() => { trackClick('hero-start'); goReg(); }}>
                    Start Selling Free <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>
                  <Button size="lg" variant="outline" className="text-base px-10 h-14 rounded-full border-white/25 text-white hover:bg-white/10" onClick={() => navigate('/how-it-works')}>
                    See How It Works
                  </Button>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ SCROLLING TICKER ══════════ */}
      <div className="overflow-hidden py-4" style={{ backgroundColor: '#0F3338' }}>
        <div className="flex whitespace-nowrap" style={{ animation: 'ticker 25s linear infinite' }}>
          {[...Array(2)].map((_, rep) => (
            <div key={rep} className="flex items-center gap-8 px-4">
              {['AI-Powered Pages', '5-Minute Setup', '0% Platform Fee', '6 Payment Gateways', 'Instant Downloads', 'Built-in Analytics', 'Click-to-Edit Builder', 'Secure File Delivery'].map(t => (
                <span key={`${rep}-${t}`} className="text-sm font-medium flex items-center gap-2" style={{ color: '#F5E9CF', opacity: 0.6 }}>
                  <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: '#FF9A00' }} /> {t}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ══════════ THE BIG IDEA ══════════ */}
      <section data-lp-section="big-idea" className="py-20 sm:py-28 px-4" style={{ backgroundColor: '#F5E9CF' }}>
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <p className="text-sm font-semibold uppercase tracking-[3px] mb-4" style={{ color: '#FF9A00' }}>The big idea</p>
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight" style={{ color: '#195259' }}>
              Other platforms give you tools.<br />Funnely gives you sales.
            </h2>
            <p className="mt-6 text-lg max-w-2xl mx-auto leading-relaxed" style={{ color: '#6D6E71' }}>
              The hard part was never uploading a file. It's getting people to buy it. Funnely's AI builds pages that convert, creates products that sell, and handles checkout so you can focus on creating.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="flex flex-col sm:flex-row gap-3 mt-10 justify-center">
              <Button className="font-semibold rounded-full h-12 px-8" style={{ backgroundColor: '#195259', color: 'white' }} onClick={() => navigate('/features')}>
                Explore Features <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
              <Button variant="outline" className="font-semibold rounded-full h-12 px-8" style={{ borderColor: '#195259', color: '#195259' }} onClick={() => navigate('/how-it-works')}>
                How It Works
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══════════ FEATURE 1: AI LANDING PAGES ══════════ */}
      <section id="features" data-lp-section="feature-lp" className="py-20 sm:py-28 px-4" style={{ backgroundColor: '#195259' }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <Reveal>
              <div>
                <p className="text-sm font-semibold uppercase tracking-[3px] mb-4" style={{ color: '#FF9A00' }}>Click-to-edit builder</p>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white leading-tight mb-6">
                  Sales pages that build themselves.
                </h2>
                <p className="text-base leading-relaxed mb-8" style={{ color: '#F5E9CF', opacity: 0.7 }}>
                  Pick from 5 premium templates or let AI write your entire page. Click any element to edit it. Change colors with one click. No design skills needed — every page is built to convert.
                </p>
                <div className="flex flex-wrap gap-3">
                  {['5 Premium Templates', 'AI Copywriting', 'Click-to-Edit', '1-Click Themes'].map(t => (
                    <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(255,154,0,0.15)', color: '#FF9A00' }}>
                      <Check className="h-3 w-3" /> {t}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="relative">
                <div className="absolute -inset-4 rounded-3xl" style={{ background: 'linear-gradient(135deg, rgba(255,154,0,0.2), transparent)', transform: 'rotate(2deg)' }} />
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10" style={{ backgroundColor: '#0F3338' }}>
                  <div className="p-4">
                    {/* Mock editor UI */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex gap-1"><div className="h-2 w-2 rounded-full bg-red-400" /><div className="h-2 w-2 rounded-full bg-yellow-400" /><div className="h-2 w-2 rounded-full bg-green-400" /></div>
                      <div className="flex-1 h-5 rounded-md" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />
                    </div>
                    <div className="grid grid-cols-5 gap-2">
                      {/* Preview area */}
                      <div className="col-span-3 rounded-lg p-3" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <div className="h-3 w-20 rounded bg-white/10 mx-auto mb-2" />
                        <div className="h-5 w-40 rounded bg-white/20 mx-auto mb-1.5" />
                        <div className="h-2 w-32 rounded bg-white/10 mx-auto mb-3" />
                        <div className="grid grid-cols-3 gap-1.5 mb-3">
                          {[1,2,3].map(i => <div key={i} className="h-12 rounded-md" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}><div className="h-3 w-3 rounded mx-auto mt-2" style={{ backgroundColor: '#FF9A00', opacity: 0.5 }} /></div>)}
                        </div>
                        <div className="h-6 w-24 rounded-md mx-auto" style={{ backgroundColor: '#FF9A00', opacity: 0.6 }} />
                      </div>
                      {/* Side panel */}
                      <div className="col-span-2 rounded-lg p-2" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <div className="flex gap-1 mb-2">{['Edit', 'Design'].map(t => <div key={t} className="flex-1 text-center py-1 rounded text-[6px] font-medium" style={{ backgroundColor: t === 'Edit' ? 'rgba(255,255,255,0.1)' : 'transparent', color: 'rgba(255,255,255,0.4)' }}>{t}</div>)}</div>
                        {[1,2,3,4].map(i => <div key={i} className="h-4 rounded bg-white/5 mb-1.5" />)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ══════════ FEATURE 2: DIGITAL STUDIO ══════════ */}
      <section data-lp-section="feature-studio" className="py-20 sm:py-28 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <Reveal delay={0.15} className="order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -inset-4 rounded-3xl" style={{ background: 'linear-gradient(135deg, rgba(25,82,89,0.1), transparent)', transform: 'rotate(-2deg)' }} />
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border" style={{ borderColor: '#195259', backgroundColor: '#F5E9CF' }}>
                  <div className="p-4">
                    {/* Mock Digital Studio UI */}
                    <p className="text-[9px] font-semibold uppercase tracking-wider mb-3" style={{ color: '#195259', opacity: 0.5 }}>AI-Generated Ebook</p>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {['Light', 'Bold', 'Dark'].map((d, i) => (
                        <div key={d} className="rounded-lg p-2 text-center transition-all" style={{ backgroundColor: i === 1 ? '#195259' : 'white', color: i === 1 ? '#F5E9CF' : '#195259', border: `1.5px solid ${i === 1 ? '#195259' : '#19525930'}` }}>
                          <div className="h-14 rounded mb-1.5" style={{ backgroundColor: i === 1 ? 'rgba(255,255,255,0.1)' : '#F5E9CF' }}>
                            <div className="pt-2 px-2"><div className="h-1.5 w-8 rounded-full mx-auto mb-1" style={{ backgroundColor: i === 1 ? 'rgba(255,255,255,0.3)' : '#19525930' }} /><div className="h-1 w-12 rounded-full mx-auto" style={{ backgroundColor: i === 1 ? 'rgba(255,255,255,0.15)' : '#19525915' }} /></div>
                          </div>
                          <p className="text-[7px] font-semibold">{d}</p>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1 h-8 rounded-lg flex items-center justify-center text-[8px] font-semibold text-white" style={{ backgroundColor: '#195259' }}>Export PDF</div>
                      <div className="flex-1 h-8 rounded-lg flex items-center justify-center text-[8px] font-semibold" style={{ border: '1.5px solid #195259', color: '#195259' }}>Export DOCX</div>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
            <Reveal className="order-1 lg:order-2">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[3px] mb-4" style={{ color: '#FF9A00' }}>AI Digital Studio</p>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight mb-6" style={{ color: '#195259' }}>
                  Create ebooks and digital products with AI.
                </h2>
                <p className="text-base leading-relaxed mb-8" style={{ color: '#6D6E71' }}>
                  Describe your idea. AI generates a complete multi-page ebook with professional layouts and stock images. Export as PDF or DOCX. From idea to sellable product in minutes.
                </p>
                <div className="flex flex-wrap gap-3">
                  {['AI-Generated Content', 'Professional Layouts', 'PDF & DOCX Export', 'Stock Images'].map(t => (
                    <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#195259', color: '#F5E9CF' }}>
                      <Check className="h-3 w-3" /> {t}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ══════════ FEATURE 3: SALES ANALYTICS ══════════ */}
      <section data-lp-section="feature-analytics" className="py-20 sm:py-28 px-4" style={{ backgroundColor: '#195259' }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <Reveal>
              <div>
                <p className="text-sm font-semibold uppercase tracking-[3px] mb-4" style={{ color: '#FF9A00' }}>Sales Analytics</p>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white leading-tight mb-6">
                  Know exactly what's working.
                </h2>
                <p className="text-base leading-relaxed mb-8" style={{ color: '#F5E9CF', opacity: 0.7 }}>
                  Track revenue, orders, page views, unique visitors, and conversion rates — all in one dashboard. Export reports, filter by date, and see which products and pages drive the most sales.
                </p>
                <div className="flex flex-wrap gap-3">
                  {['Revenue Tracking', 'Conversion Rates', 'Visitor Analytics', 'Export Reports'].map(t => (
                    <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(255,154,0,0.15)', color: '#FF9A00' }}>
                      <Check className="h-3 w-3" /> {t}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="relative">
                <div className="absolute -inset-4 rounded-3xl" style={{ background: 'linear-gradient(135deg, rgba(255,154,0,0.15), transparent)', transform: 'rotate(2deg)' }} />
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10" style={{ backgroundColor: '#0F3338' }}>
                  <div className="p-4">
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {[['Total Revenue', 'RM 12,450'], ['Total Orders', '284'], ['Conversion', '4.2%']].map(([l, v]) => (
                        <div key={l} className="rounded-lg p-3 text-center" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                          <p className="text-[8px] uppercase tracking-wider" style={{ color: '#F5E9CF', opacity: 0.5 }}>{l}</p>
                          <p className="text-sm font-bold text-white mt-1">{v}</p>
                        </div>
                      ))}
                    </div>
                    <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <p className="text-[9px] font-semibold text-white/60 mb-2">Revenue Trend</p>
                      <div className="flex items-end gap-1 h-16">
                        {[30, 45, 35, 60, 50, 75, 65, 85, 70, 90, 80, 95].map((h, i) => (
                          <div key={i} className="flex-1 rounded-sm transition-all" style={{ height: `${h}%`, backgroundColor: i === 11 ? '#FF9A00' : 'rgba(255,154,0,0.3)' }} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ══════════ FEATURE 4: SECURE DELIVERY ══════════ */}
      <section data-lp-section="feature-delivery" className="py-20 sm:py-28 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <Reveal delay={0.15} className="order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -inset-4 rounded-3xl" style={{ background: 'linear-gradient(135deg, rgba(25,82,89,0.08), transparent)', transform: 'rotate(-2deg)' }} />
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border" style={{ borderColor: '#195259', backgroundColor: '#F5E9CF' }}>
                  <div className="p-5">
                    <div className="rounded-xl p-4 bg-white mb-3" style={{ border: '1px solid #19525920' }}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#195259' }}><ShieldCheck className="h-5 w-5 text-white" /></div>
                        <div><p className="text-xs font-bold" style={{ color: '#195259' }}>Secure Download Link</p><p className="text-[9px]" style={{ color: '#6D6E71' }}>Auto-generated after payment</p></div>
                      </div>
                      <div className="rounded-lg p-2.5" style={{ backgroundColor: '#F5E9CF' }}>
                        <p className="text-[8px] font-mono truncate" style={{ color: '#195259' }}>https://funnely.com/dl/a8f3k2...</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {[['5 downloads', Package], ['72h expiry', Clock], ['Auto-delivery', Zap]].map(([label, Icon]) => (
                        <div key={label} className="flex-1 rounded-lg p-2 text-center bg-white" style={{ border: '1px solid #19525915' }}>
                          <Icon className="h-3.5 w-3.5 mx-auto mb-1" style={{ color: '#FF9A00' }} />
                          <p className="text-[8px] font-medium" style={{ color: '#195259' }}>{label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
            <Reveal className="order-1 lg:order-2">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[3px] mb-4" style={{ color: '#FF9A00' }}>Automatic delivery</p>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight mb-6" style={{ color: '#195259' }}>
                  Secured and automatic file delivery.
                </h2>
                <p className="text-base leading-relaxed mb-8" style={{ color: '#6D6E71' }}>
                  Every purchase generates a unique, time-limited download link automatically. Set download limits, expiry windows, and let the system handle delivery — so you never have to send a file manually again.
                </p>
                <div className="flex flex-wrap gap-3">
                  {['Auto-Generated Links', 'Download Limits', 'Time-Limited Access', 'Zero Manual Work'].map(t => (
                    <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#195259', color: '#F5E9CF' }}>
                      <Check className="h-3 w-3" /> {t}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ══════════ FEATURE 5: AI ENGINE ══════════ */}
      <section data-lp-section="feature-ai" className="py-20 sm:py-28 px-4" style={{ backgroundColor: '#195259' }}>
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <p className="text-sm font-semibold uppercase tracking-[3px] mb-4" style={{ color: '#FF9A00' }}>AI-Powered</p>
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight mb-6">
              Your AI business partner.
            </h2>
            <p className="text-lg max-w-2xl mx-auto leading-relaxed mb-12" style={{ color: '#F5E9CF', opacity: 0.7 }}>
              From writing sales copy to generating ebooks to advising your growth strategy — AI handles the work that used to take hours.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: Wand2, title: 'AI Page Builder', desc: 'Describe your business. Get a complete, high-converting sales page.' },
              { icon: BookOpen, title: 'AI Ebook Creator', desc: 'Generate multi-page ebooks with layouts, images, and content.' },
              { icon: Bot, title: 'AI Growth Consultant', desc: 'Chat with AI trained on marketing strategy. Get real-time advice.' },
            ].map((f, i) => (
              <Reveal key={f.title} delay={i * 0.1}>
                <div className="rounded-2xl p-6 text-center transition-all duration-300 hover:-translate-y-1" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>
                  <div className="h-12 w-12 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#FF9A00' }}>
                    <f.icon className="h-6 w-6 text-[#195259]" />
                  </div>
                  <h3 className="font-bold text-sm text-white mb-2">{f.title}</h3>
                  <p className="text-xs leading-relaxed" style={{ color: '#F5E9CF', opacity: 0.6 }}>{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ HOW IT WORKS ══════════ */}
      <section id="how-it-works" data-lp-section="how-it-works" className="py-20 sm:py-28 px-4" style={{ backgroundColor: '#F5E9CF' }}>
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-sm font-semibold uppercase tracking-[3px] mb-4" style={{ color: '#FF9A00' }}>How it works</p>
              <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight" style={{ color: '#195259' }}>Three steps. That's it.</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { num: '01', img: '/images/how-it-works/create-product.png', title: 'Create your product', desc: 'Upload or build your digital product. Set your price and download limits.' },
              { num: '02', img: '/images/how-it-works/build-page.png', title: 'Build your page', desc: 'Pick a template or let AI do it. Click to edit anything.' },
              { num: '03', img: '/images/how-it-works/share-sell.png', title: 'Share and sell', desc: 'Copy your link. Paste in bio. Start making money.' },
            ].map((step, i) => (
              <Reveal key={step.num} delay={i * 0.1}>
                <div className="bg-white rounded-2xl p-8 text-center h-full transition-all duration-300 hover:-translate-y-1" style={{ border: '1.5px solid #195259', boxShadow: '0 8px 24px rgba(0,0,0,0.07)' }}>
                  <div className="h-10 w-10 rounded-full flex items-center justify-center text-sm font-extrabold text-white mx-auto mb-5" style={{ backgroundColor: '#195259' }}>{step.num}</div>
                  <div className="flex items-center justify-center mb-5">
                    <img src={step.img} alt={step.title} loading="lazy" className="w-full max-w-[220px] h-[180px] object-contain" />
                  </div>
                  <h3 className="text-lg font-bold mb-3" style={{ color: '#195259' }}>{step.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#6D6E71' }}>{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="text-center mt-12">
              <Button size="lg" className="text-sm px-8 h-12 font-semibold rounded-full" style={{ backgroundColor: '#195259', color: 'white' }} onClick={() => navigate('/how-it-works')}>
                See Full Guide <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══════════ EVERYTHING ELSE — Feature Grid ══════════ */}
      <section data-lp-section="features-grid" className="py-20 sm:py-28 px-4" style={{ backgroundColor: '#195259' }}>
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-sm font-semibold uppercase tracking-[3px] mb-4" style={{ color: '#FF9A00' }}>Everything else</p>
              <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white">The full platform, from day one.</h2>
              <p className="mt-4 text-lg" style={{ color: '#F5E9CF', opacity: 0.6 }}>Every feature included. No add-ons. No surprises.</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES_GRID.map((f, i) => (
              <Reveal key={f.title} delay={Math.min(i * 0.05, 0.3)}>
                <div className="rounded-2xl p-6 h-full transition-all duration-300 hover:bg-white/10 hover:-translate-y-1" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>
                  <div className="h-11 w-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: '#FF9A00' }}>
                    <f.icon className="h-5 w-5 text-[#195259]" />
                  </div>
                  <h3 className="font-bold text-sm mb-2 text-white">{f.title}</h3>
                  <p className="text-xs leading-relaxed" style={{ color: '#F5E9CF', opacity: 0.6 }}>{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="text-center mt-12">
              <Button size="lg" className="text-sm px-8 h-12 font-semibold rounded-full border-2" style={{ backgroundColor: 'transparent', color: '#FF9A00', borderColor: '#FF9A00' }} onClick={() => navigate('/features')}>
                See All Features <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══════════ TESTIMONIALS ══════════ */}
      <section data-lp-section="testimonials" className="py-20 sm:py-28 px-4 overflow-hidden" style={{ backgroundColor: '#FF9A00' }}>
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: '#195259' }}>Creators who made the switch.</h2>
              <p className="mt-3" style={{ color: '#195259', opacity: 0.7 }}>Real businesses. Real results.</p>
            </div>
          </Reveal>
          <div className="relative">
            <button onClick={() => setTestimonialIdx(p => p <= 0 ? maxIdx : p - 1)} className="absolute left-0 top-1/2 -translate-y-1/2 z-20 hover:scale-110 transition-transform"><ChevronLeft className="h-8 w-8" style={{ color: '#195259' }} /></button>
            <button onClick={() => setTestimonialIdx(p => p >= maxIdx ? 0 : p + 1)} className="absolute right-0 top-1/2 -translate-y-1/2 z-20 hover:scale-110 transition-transform"><ChevronRightIcon className="h-8 w-8" style={{ color: '#195259' }} /></button>
            <div className="overflow-hidden mx-10">
              <div className="flex transition-transform duration-500 ease-in-out" style={{ width: `${(TESTIMONIALS.length / slidesPerView) * 100}%`, transform: `translateX(-${testimonialIdx * (100 / TESTIMONIALS.length)}%)` }}>
                {TESTIMONIALS.map((t, i) => (
                  <div key={`${t.name}-${i}`} className="shrink-0 px-2" style={{ width: `${100 / TESTIMONIALS.length}%` }}>
                    <div className="bg-white rounded-2xl p-6 h-full" style={{ border: '1.5px solid #195259', boxShadow: '0 8px 24px rgba(0,0,0,0.07)' }}>
                      <div className="flex items-center gap-1 mb-3">{[1,2,3,4,5].map(s => <Star key={s} className="h-3.5 w-3.5 fill-[#FF9A00] text-[#FF9A00]" />)}</div>
                      <p className="text-sm mb-5 leading-relaxed" style={{ color: '#195259' }}>"{t.text}"</p>
                      <div className="flex items-center gap-2">
                        <div className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: '#195259' }}>{t.avatar}</div>
                        <div><p className="text-xs font-bold" style={{ color: '#195259' }}>{t.name}</p><p className="text-[10px]" style={{ color: '#6D6E71' }}>{t.role}</p></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-center gap-2 mt-8">{Array.from({ length: maxIdx + 1 }).map((_, i) => <button key={i} onClick={() => setTestimonialIdx(i)} className="h-2.5 rounded-full transition-all duration-200" style={{ width: testimonialIdx === i ? '24px' : '10px', backgroundColor: testimonialIdx === i ? '#195259' : 'rgba(25,82,89,0.3)' }} />)}</div>
          </div>
        </div>
      </section>

      {/* ══════════ PRICING ══════════ */}
      <section id="pricing" data-lp-section="pricing" className="py-20 sm:py-28 px-4" style={{ backgroundColor: '#F5E9CF' }}>
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-sm font-semibold uppercase tracking-[3px] mb-4" style={{ color: '#FF9A00' }}>Simple pricing</p>
              <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight" style={{ color: '#195259' }}>Transparent pricing. No surprises.</h2>
              <p className="mt-4 text-lg" style={{ color: '#6D6E71' }}>No hidden fees. No platform transaction charges. Cancel anytime.</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {PRICING.map((plan, pi) => (
              <Reveal key={plan.name} delay={pi * 0.1}>
                <div className={`rounded-2xl p-7 relative bg-white h-full ${plan.popular ? 'scale-105 shadow-xl' : 'shadow-lg'}`} style={{ border: `1.5px solid ${plan.popular ? '#FF9A00' : '#195259'}` }}>
                  {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><Badge className="px-4 text-white font-semibold" style={{ backgroundColor: '#FF9A00' }}>Most Popular</Badge></div>}
                  <h3 className="text-lg font-bold" style={{ color: '#195259' }}>{plan.name}</h3>
                  <p className="text-xs mt-1" style={{ color: '#6D6E71' }}>{plan.desc}</p>
                  <div className="my-5">
                    {plan.name === 'Custom' ? (
                      <><span className="text-sm" style={{ color: '#6D6E71' }}>Starting from </span><span className="text-4xl font-extrabold" style={{ color: '#195259' }}>RM {plan.price}</span></>
                    ) : (
                      <><span className="text-4xl font-extrabold" style={{ color: '#195259' }}>RM {plan.price}</span><span className="text-sm" style={{ color: '#6D6E71' }}>{plan.period}</span></>
                    )}
                  </div>
                  <Button className="w-full mb-5 h-11 font-semibold rounded-xl" style={plan.popular ? { backgroundColor: '#FF9A00', color: '#195259' } : { backgroundColor: '#195259', color: 'white' }} onClick={() => { trackClick(`pricing-${plan.name.toLowerCase()}`); plan.ctaAction === 'contact' ? window.open('mailto:hello@myfunnely.com', '_blank') : goReg(); }}>{plan.cta}</Button>
                  <div className="space-y-2.5">
                    {plan.features.map(f => <div key={f} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 shrink-0" style={{ color: '#FF9A00' }} /><span style={{ color: '#195259' }}>{f}</span></div>)}
                    {plan.notIncluded.map(f => <div key={f} className="flex items-center gap-2 text-sm"><X className="h-4 w-4 shrink-0 text-gray-300" /><span className="text-gray-400">{f}</span></div>)}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ FAQ ══════════ */}
      <section data-lp-section="faq" className="py-20 sm:py-28 px-4 bg-white">
        <div className="max-w-2xl mx-auto">
          <Reveal>
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: '#195259' }}>Got questions?</h2>
              <p className="mt-3" style={{ color: '#6D6E71' }}>We've got answers.</p>
            </div>
          </Reveal>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <Reveal key={faq.q} delay={Math.min(i * 0.05, 0.2)}>
                <div className="rounded-xl overflow-hidden bg-white" style={{ border: '1.5px solid #195259', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                  <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left hover:bg-[#F5E9CF]/40 transition-colors">
                    <span className="font-semibold text-sm" style={{ color: '#195259' }}>{faq.q}</span>
                    {expandedFaq === i ? <ChevronUp className="h-4 w-4 shrink-0" style={{ color: '#FF9A00' }} /> : <ChevronDown className="h-4 w-4 shrink-0" style={{ color: '#6D6E71' }} />}
                  </button>
                  {expandedFaq === i && <div className="px-5 pb-5"><p className="text-sm leading-relaxed" style={{ color: '#6D6E71' }}>{faq.a}</p></div>}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ FINAL CTA ══════════ */}
      <section data-lp-section="final-cta" className="py-24 sm:py-32 px-4" style={{ backgroundColor: '#195259' }}>
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <img src="/images/marketing/cta-mascot.png" alt="" loading="lazy" className="w-[200px] sm:w-[260px] h-auto mx-auto mb-8 object-contain" style={{ animation: 'float 4s ease-in-out infinite' }} />
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white mb-5">Ready to start selling?</h2>
            <p className="text-lg mb-10" style={{ color: '#F5E9CF', opacity: 0.8 }}>Set up in 5 minutes. Your digital store awaits.</p>
            <Button size="lg" className="text-base px-12 h-14 font-semibold text-[#195259] rounded-full shadow-lg hover:shadow-xl transition-shadow" style={{ backgroundColor: '#FF9A00' }} onClick={() => { trackClick('cta-final'); goReg(); }}>
              Create Your Store Now <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </Reveal>
        </div>
      </section>

      {/* ══════════ FOOTER ══════════ */}
      <footer className="py-10 px-4" style={{ backgroundColor: '#0F3338' }}>
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div><img src="/logos/funnely-white.png" alt="Funnely" className="h-5 object-contain" /><p className="text-xs mt-1" style={{ color: '#6D6E71' }}>The easiest way to sell digital products.</p></div>
          <div className="flex items-center gap-6 text-sm" style={{ color: '#6D6E71' }}>
            <a href="/features" className="hover:text-white transition-colors">Features</a>
            <a href="/pricing" className="hover:text-white transition-colors">Pricing</a>
            <button onClick={() => navigate('/login')} className="hover:text-white transition-colors">Log In</button>
          </div>
          <p className="text-xs" style={{ color: '#6D6E71' }}>&copy; 2026 Funnely. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
